// File: frontend/src/pages/auth/OAuthCallback.jsx
// AUDIT FIX (C-3, F-3): no more tokens in URL, no more console.log of secrets.
// Backend now redirects with ?code=<opaque> only; we POST it to
// /api/auth/oauth/exchange to receive the tokens.

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/index.js';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import toast from 'react-hot-toast';
import { CheckCircle, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function _readCsrfCookie() {
  try {
    const m = document.cookie.match(/(?:^|;\s*)scr_csrf=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch { return null; }
}
let _csrfCache = null;
async function _primeCsrf(baseUrl) {
  if (_csrfCache) return _csrfCache;
  try {
    const r = await fetch(`${baseUrl}/csrf`, { credentials: 'include' });
    if (r.ok) {
      const j = await r.json();
      _csrfCache = (j && j.csrfToken) || _readCsrfCookie();
    }
  } catch (_) { _csrfCache = _readCsrfCookie(); }
  return _csrfCache;
}
async function __secureFetch(url, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const isWrite = !['GET', 'HEAD', 'OPTIONS'].includes(method);
  let token = _csrfCache || _readCsrfCookie();
  if (isWrite && !token) {
    const baseMatch = url.match(/^(https?:\/\/[^/]+\/api)/) || [];
    if (baseMatch[1]) token = await _primeCsrf(baseMatch[1]);
  }
  const finalOpts = {
    ...options,
    credentials: 'include',
    headers: {
      ...(options.headers || {}),
      ...(token && isWrite ? { 'X-CSRF-Token': token } : {}),
    },
  };
  return fetch(url, finalOpts);
}

const OAuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithOAuth, updateUser } = useAuth();
  const [status, setStatus] = useState('processing');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!document.getElementById('hp2-font-oauth')) {
      const link = document.createElement('link');
      link.id = 'hp2-font-oauth';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Inter:wght@300;400;500&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const error = searchParams.get('error');
        if (error) {
          setStatus('error');
          setErrorMessage(decodeURIComponent(error));
          toast.error(decodeURIComponent(error));
          setTimeout(() => navigate('/login', { replace: true }), 3000);
          return;
        }

        const code = searchParams.get('code');
        if (!code) {
          setStatus('error');
          setErrorMessage('Invalid authentication response');
          toast.error('Authentication failed. Please try again.');
          setTimeout(() => navigate('/login', { replace: true }), 3000);
          return;
        }

        const res = await __secureFetch(`${API_URL}/auth/oauth/exchange`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json?.data?.accessToken) {
          setStatus('error');
          setErrorMessage(json?.error?.message || 'Exchange failed');
          toast.error('Authentication failed. Please try again.');
          setTimeout(() => navigate('/login', { replace: true }), 3000);
          return;
        }

        const { accessToken, refreshToken, userId, name, email, role, avatar, provider, isNewUser } = json.data;

        const userData = {
          _id: userId,
          id: userId,
          name: name || '',
          email: email || '',
          role: role || 'customer',
          avatar: avatar || null,
          authProvider: provider || 'oauth',
          emailVerified: true,
          isNewUser: !!isNewUser,
        };

        if (loginWithOAuth) {
          loginWithOAuth(userData, { accessToken, refreshToken });
        } else {
          localStorage.setItem('authToken', accessToken);
          localStorage.setItem('refreshToken', refreshToken);
          localStorage.setItem('user', JSON.stringify(userData));
          if (updateUser) updateUser(userData);
        }

        try { window.history.replaceState({}, '', '/oauth/callback'); } catch (_) {}

        setStatus('success');
        toast.success(
          isNewUser
            ? `Welcome to Swat Capital Resort${name ? `, ${name}` : ''}!`
            : `Welcome back${name ? `, ${name}` : ''}!`,
          { duration: 3000 }
        );

        const returnUrl = localStorage.getItem('oauthReturnUrl') || '/';
        localStorage.removeItem('oauthReturnUrl');
        setTimeout(() => navigate(returnUrl, { replace: true }), 100);
      } catch (err) {
        setStatus('error');
        setErrorMessage('An unexpected error occurred');
        toast.error('Authentication failed. Please try again.');
        setTimeout(() => navigate('/login', { replace: true }), 3000);
      }
    };
    handleCallback();
  }, [searchParams, navigate, loginWithOAuth, updateUser]);

  return (
    <>
      <style>{`
        .oc2 {
          --oc2-black: #0D0D0D;
          --oc2-cream: #E2D0B0;
          --oc2-cream-mid: #C8B48E;
          --oc2-gold: #C4A25A;
          --oc2-gold-l: #D4B870;
          --oc2-muted: #6B6B6B;
          --oc2-border: rgba(196,162,90,0.22);
          font-family: 'Inter', system-ui, sans-serif;
          min-height: 100vh;
          background: var(--oc2-black);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem 1.5rem;
          position: relative;
        }
        .oc2::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse 60% 50% at 50% 0%, rgba(196,162,90,0.07) 0%, transparent 70%);
          pointer-events: none;
        }
        .oc2-card {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 400px;
          border: 1px solid var(--oc2-border);
          background: rgba(255,255,255,0.02);
          padding: 3rem 2.5rem;
          text-align: center;
        }
        .oc2-logo {
          width: 44px;
          height: 44px;
          border: 1px solid var(--oc2-border);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 2.5rem;
        }
        .oc2-logo svg {
          width: 20px;
          height: 20px;
          stroke: var(--oc2-gold);
          fill: none;
        }
        .oc2-heading {
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-size: 1.7rem;
          font-weight: 300;
          color: var(--oc2-cream);
          margin: 0 0 0.625rem;
          line-height: 1.2;
        }
        .oc2-heading em { font-style: italic; color: var(--oc2-gold); }
        .oc2-body {
          font-size: 0.85rem;
          color: var(--oc2-muted);
          font-weight: 300;
          line-height: 1.65;
          margin: 0 0 1.5rem;
        }
        .oc2-spinner-wrap {
          margin: 0 auto 1.75rem;
          display: flex;
          justify-content: center;
        }
        .oc2-icon-wrap {
          margin: 0 auto 1.5rem;
          display: flex;
          justify-content: center;
        }
        .oc2-icon-wrap svg {
          width: 52px;
          height: 52px;
        }
        .oc2-divider {
          width: 36px;
          height: 1px;
          background: var(--oc2-gold);
          opacity: 0.4;
          margin: 0 auto 1.5rem;
        }
        .oc2-btn {
          width: 100%;
          padding: 0.875rem;
          background: var(--oc2-gold);
          color: var(--oc2-black);
          font-family: 'Inter', sans-serif;
          font-size: 0.72rem;
          font-weight: 500;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          border: none;
          cursor: pointer;
          transition: background 0.2s;
          margin-top: 0.5rem;
        }
        .oc2-btn:hover { background: var(--oc2-gold-l); }
        .oc2-dots {
          display: inline-flex;
          gap: 6px;
          margin-bottom: 1.5rem;
        }
        .oc2-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: var(--oc2-gold);
          animation: oc2-bounce 1.2s ease-in-out infinite;
          opacity: 0.7;
        }
        .oc2-dot:nth-child(2) { animation-delay: 0.2s; }
        .oc2-dot:nth-child(3) { animation-delay: 0.4s; }
        @keyframes oc2-bounce {
          0%, 80%, 100% { transform: scale(0.7); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>

      <div className="oc2">
        <div className="oc2-card">
          <div className="oc2-logo">
            <svg viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
            </svg>
          </div>

          {status === 'processing' && (
            <>
              <div className="oc2-dots">
                <div className="oc2-dot" />
                <div className="oc2-dot" />
                <div className="oc2-dot" />
              </div>
              <h2 className="oc2-heading">Completing <em>sign in</em></h2>
              <p className="oc2-body">Please wait while we verify your account.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="oc2-icon-wrap">
                <CheckCircle style={{ width: 52, height: 52, stroke: 'var(--oc2-gold)', fill: 'none' }} aria-hidden="true" />
              </div>
              <div className="oc2-divider" />
              <h2 className="oc2-heading"><em>Welcome</em></h2>
              <p className="oc2-body">Redirecting you now…</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="oc2-icon-wrap">
                <XCircle style={{ width: 52, height: 52, stroke: '#C4A25A', fill: 'none', opacity: 0.8 }} aria-hidden="true" />
              </div>
              <div className="oc2-divider" />
              <h2 className="oc2-heading">Sign in <em>failed</em></h2>
              <p className="oc2-body">{errorMessage || 'An error occurred during sign in.'}</p>
              <button onClick={() => navigate('/login')} className="oc2-btn">
                Back to Login
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default OAuthCallback;
